import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import { ritzDataContext, setLoginInfo } from '@moderepo/mode-data-state-ritz';
import { getErrorMessage, setLocalStorageItem, useQueryParams } from '@moderepo/mode-app-components';
import { ApiError } from '@moderepo/mode-apis';
import { RitzAPI } from '@moderepo/mode-internal-apis';
import { RouteHelper } from '..';
import * as Constants from '../../utils/constants';
import { useRitzTranslation } from '../../utils/customHooks';


/**
 * Verify Agent page. This page is used for verifying agent account after the agent changed his email. This page should only be
 * loaded from clicking on verify account email.
 * NOTE that this page doesn't have any UI. All it does is make an API call to verify the agent. If successful, it will redirect
 * the agent to the home page. If not, it takes the agent to the login page.
 *
 * @param param
 */
export const VerifyAgent: React.FC = () => {
    const navigate = useNavigate();
    const queryParams = useQueryParams();

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { dispatch:dataDispatch } = useContext(ritzDataContext);
    const routeHelper = RouteHelper.getInstance(navigate);
    const { trans } = useRitzTranslation(undefined);

    const token = queryParams.get('token');
    const verifyAgentCalled = useRef<boolean>(false);


    useEffect(() => {
        const onVerifyAgent = async () => {
            if (token) {
                verifyAgentCalled.current = true;

                try {
                    const loginInfo = await RitzAPI.getInstance().verifyAgent(token);
                    setLocalStorageItem(Constants.LS_KEY_LOGIN_INFO, loginInfo);
                    await dataDispatch(setLoginInfo(loginInfo));

                    // Do nothing, the user will be taken to the home page automatically when login info is added to the state
                } catch (error) {
                    uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                    routeHelper.gotoLoginPage(true);
                }
            }
        };

        if (!verifyAgentCalled.current) {
            onVerifyAgent();
        }

    }, [dataDispatch, routeHelper, token, uiDispatch]);


    useEffect(() => {
        // Check the URL params for token. If token is not in the URL, redirect the agent to login page
        if (!token) {
            // Missing 'token' in the URL params, need to take agent to login page
            uiDispatch(showErrorNotification(trans('notif.invalid_verify_token'), undefined, 10000));
            routeHelper.gotoLoginPage(true);
        }
    }, [routeHelper, token, trans, uiDispatch]);


    return (
        <></>
    );
};
